import { useRef, useState, useEffect } from 'react';
import { func } from 'prop-types';
import styled from 'styled-components/macro';

const Root = styled.div`
  flex: 0 0 auto;
  width: 100%;
  height: 100%;
  overscroll-behavior-y: contain;
`;

/**
 * Responsive SVG renders an SVG element in a root element.
 * A div will fill its available width by default. Additionally,
 * this div has a set height. The size of the div is passed
 * to the SVG by a ref to the root element and through the state of
 * the component, which is contained in a hook.
 *
 * The element calls its children as a function, allowing you to use the
 * size of the root as render-props for the child components of the SVG.
 *
 * @param       {Object} props - React props
 * @param       {Function} props.children - a render props function
 */
function ResponsiveSVG(props) {
  // An empty ref to the root element
  const root = useRef(null);

  // Initialize the size state hook
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    // Listen for changes to the window size, and update the state when it changes.
    window.addEventListener('resize', handleResize);

    // Call handleResize once to set the initial size.
    handleResize();

    /**
     * handleResize sets the size of the root element in the state if it
     * differs from the current size in state.
     */
    function handleResize() {
      const { width, height } = root.current.getBoundingClientRect();
      setSize({ width, height });
    }

    // This returned function is called when the component is unmounted.
    return function cleanup() {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Root ref={root}>
      <svg
        {...size}
        viewBox={`0 0 ${size.width} ${size.height}`}
        style={{ overflow: 'hidden' }}
      >
        {props.children(size)}
      </svg>
    </Root>
  );
}

ResponsiveSVG.propTypes = {
  children: func.isRequired,
};

export default ResponsiveSVG;
